/**
 * Shared ApexCharts theme for the CRM analytics surface.
 *
 * Every other chart in this codebase declares its options inline, which is why
 * the app currently carries three different "brand" purples (#6B43B5, #8b5cf6,
 * and Apex's defaults). This is the one place CRM chart colour and mark rules
 * live — extend it rather than adding a fourth.
 *
 * PALETTE — validated, not eyeballed. These four passed all six checks of the
 * dataviz validator against a light surface: lightness band, chroma floor,
 * colour-vision-deficiency separation, normal-vision separation, and >= 3:1
 * contrast. Re-run the validator before changing any of them.
 *
 * Four FAMILIES rather than seven series is deliberate: a seven-segment stacked
 * bar can't be read. Hue carries the family in the headline chart; the seven
 * individual metrics get single-series panels where the title carries identity
 * and hue is redundant.
 */

export const FAMILY_COLORS = {
  requests: "#6B43B5", // brand purple — the anchor
  manual: "#C2670A",
  ai: "#0E8FA8",
  views: "#C2255C",
};

export const FAMILY_LABELS = {
  requests: "Requests",
  manual: "Manual entries",
  ai: "AI checks",
  views: "Viewings",
};

export const FAMILY_ORDER = ["requests", "ai", "manual", "views"];

// Populations are a different dimension from metric family, so they get their
// own encoding rather than borrowing a family hue.
export const POPULATION_COLORS = { ftc: "#6B43B5", office: "#0E8FA8" };

const INK = "#2b2440";
const MUTED = "#8a8699";
const GRID = "#efedf5";
const SURFACE = "#ffffff";

/** Figures line up in columns, so digits must be fixed-width. */
export const NUMERIC = { fontVariantNumeric: "tabular-nums" };

const baseChart = {
  toolbar: { show: false },
  zoom: { enabled: false },
  fontFamily: "inherit",
  foreColor: MUTED,
  animations: { speed: 220 },
};

const baseGrid = {
  borderColor: GRID,
  strokeDashArray: 3,
  padding: { left: 4, right: 4, top: 0 },
};

/** Text wears text tokens, never the series colour. */
const baseTooltip = { theme: "light", style: { fontSize: "12px" } };

/**
 * Stacked columns for the headline "what happened over time" view.
 * Buckets are discrete (a day, a week, a month), so columns — an area chart
 * would imply a continuous signal that isn't there.
 */
export function stackedColumnOptions(categories, familyKeys) {
  return {
    chart: { ...baseChart, type: "bar", stacked: true, height: 300 },
    colors: familyKeys.map((k) => FAMILY_COLORS[k]),
    plotOptions: {
      bar: {
        columnWidth: "58%",
        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
      },
    },
    // A 2px surface-coloured stroke is what separates stacked segments; without
    // it adjacent fills read as one block.
    stroke: { show: true, width: 2, colors: [SURFACE] },
    dataLabels: { enabled: false },
    grid: baseGrid,
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "11px" } },
    },
    yaxis: { labels: { style: { fontSize: "11px" } }, forceNiceScale: true },
    legend: { position: "bottom", horizontalAlign: "left", markers: { radius: 3 }, itemMargin: { horizontal: 10 } },
    tooltip: { ...baseTooltip, shared: true, intersect: false },
    noData: { text: "No activity in this window", style: { color: MUTED, fontSize: "13px" } },
  };
}

/** One metric, one panel — identity comes from the panel title, not the hue. */
export function sparkColumnOptions(categories, family) {
  return {
    chart: { ...baseChart, type: "bar", height: 120, sparkline: { enabled: false } },
    colors: [FAMILY_COLORS[family] || FAMILY_COLORS.requests],
    plotOptions: { bar: { columnWidth: "62%", borderRadius: 3, borderRadiusApplication: "end" } },
    dataLabels: { enabled: false },
    grid: { ...baseGrid, show: false },
    xaxis: {
      categories,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: { show: false },
    legend: { show: false },       // a single series needs no legend — the title names it
    tooltip: { ...baseTooltip, x: { show: true } },
  };
}

/** Per-user ranking. Horizontal because names are long and order is the point. */
export function userBarOptions(names, family) {
  return {
    chart: { ...baseChart, type: "bar", height: Math.max(140, names.length * 34 + 40) },
    colors: [POPULATION_COLORS[family] || FAMILY_COLORS.requests],
    plotOptions: { bar: { horizontal: true, barHeight: "56%", borderRadius: 4, borderRadiusApplication: "end" } },
    // Few enough marks that a value on each is informative rather than noise.
    dataLabels: {
      enabled: true,
      offsetX: 18,
      style: { fontSize: "11px", colors: [INK], fontWeight: 600 },
    },
    grid: { ...baseGrid, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    xaxis: { categories: names, axisBorder: { show: false }, axisTicks: { show: false }, labels: { show: false } },
    yaxis: { labels: { style: { fontSize: "12px", colors: INK } } },
    legend: { show: false },
    tooltip: { ...baseTooltip },
  };
}

/** Tiny trend line for the drawer tiles — no axes, no chrome. */
export function tileSparkOptions(family) {
  return {
    chart: { ...baseChart, type: "area", height: 34, sparkline: { enabled: true } },
    colors: [FAMILY_COLORS[family] || FAMILY_COLORS.requests],
    stroke: { width: 2, curve: "smooth" },
    fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.02 } },
    tooltip: { enabled: false },
  };
}

/** Shorter labels as the window grows, so the axis never collides with itself. */
export function formatBucket(iso, period) {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  if (period === "monthly") return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
